import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { AICopilot } from '../AICopilot';
import { useEffect, useMemo, useState } from 'react';
import { Menu, Search, X } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useModuleLoader } from '../../context/ModuleLoaderContext';
import { useNavigationProfile } from '../../hooks/useNavigationProfile';
import { usePageUsageAnalytics } from '../../hooks/usePageUsageAnalytics';
import { isEnabledFlag } from '../../lib/featureFlags';
import { canonicalizeNavigationPath } from '../../lib/navigationPaths';
import { API_BASE_URL } from '../../config/api';

interface VersionInfo {
    version: string;
    buildTime: string;
    commit: string;
}

export function AppLayout() {
    const [version, setVersion] = useState<VersionInfo | null>(null);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [query, setQuery] = useState('');
    const { getSetting } = useSettings();
    const { activeModules, modules } = useModuleLoader();
    const enabledModuleCodes = useMemo(() => new Set(activeModules.map((module) => module.id)), [activeModules]);
    const navigate = useNavigate();
    const location = useLocation();
    const navigationV2Enabled = isEnabledFlag(getSetting('ui_navigation_v2_enabled'));
    const copilotRaw = getSetting('copilot_enabled');
    const copilotEnabled = copilotRaw === '' || isEnabledFlag(copilotRaw);
    const { profile, pinnedViews, removePinnedView, trackNavigation } = useNavigationProfile(navigationV2Enabled);
    usePageUsageAnalytics(true);

    useEffect(() => {
        const fetchVersion = async () => {
            const candidates = [`${API_BASE_URL}/version.json`, '/api/version.json', '/version.json'];
            for (const url of candidates) {
                try {
                    const res = await fetch(url);
                    if (!res.ok) continue;
                    const data = await res.json();
                    setVersion(data);
                    return;
                } catch {
                    // try next candidate
                }
            }
            setVersion({ version: 'dev', buildTime: new Date().toISOString(), commit: 'local' });
        };
        fetchVersion();
    }, []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setPaletteOpen((prev) => !prev);
            }
            if (event.key === 'Escape') {
                setPaletteOpen(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (!navigationV2Enabled) return;
        trackNavigation(location.pathname, location.pathname, 'route');
    }, [location.pathname, navigationV2Enabled, trackNavigation]);

    useEffect(() => {
        setMobileSidebarOpen(false);
    }, [location.pathname]);

    const commandItems = useMemo(() => {
        const mergedModules = [
            ...activeModules,
            ...modules
                .filter((m) => enabledModuleCodes.has(m.id) && !activeModules.find((am) => am.id === m.id))
                .map((m) => ({ id: m.id, name: m.name, menus: m.menus || [], enabled: true })),
        ];
        const seen = new Set<string>();
        const out: Array<{ path: string; label: string; group: string }> = [];
        for (const moduleDef of mergedModules) {
            for (const menu of moduleDef.menus || []) {
                const key = canonicalizeNavigationPath(menu.path);
                if (seen.has(key)) continue;
                seen.add(key);
                out.push({ path: menu.path, label: menu.label, group: moduleDef.name });
            }
        }
        for (const item of profile.recent_paths || []) {
            const key = canonicalizeNavigationPath(item.path);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ path: item.path, label: item.label || item.path, group: 'Recent' });
        }
        for (const path of profile.pinned_paths || []) {
            const key = canonicalizeNavigationPath(path);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ path, label: path, group: 'Pinned' });
        }
        return out;
    }, [activeModules, enabledModuleCodes, modules, profile.pinned_paths, profile.recent_paths]);

    const filteredCommands = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return commandItems.slice(0, 20);
        return commandItems
            .filter((item) => item.label.toLowerCase().includes(q) || item.path.toLowerCase().includes(q) || item.group.toLowerCase().includes(q))
            .slice(0, 20);
    }, [commandItems, query]);

    const sortedPinnedViews = useMemo(
        () =>
            [...(pinnedViews || [])].sort(
                (left, right) =>
                    new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime()
            ),
        [pinnedViews]
    );

    return (
        <div className="flex h-screen bg-slate-50 w-full overflow-hidden">
            <Sidebar version={version} className="hidden lg:flex" />
            <button
                type="button"
                aria-label="Open navigation"
                className="fixed left-3 top-3 z-40 inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-sm lg:hidden"
                onClick={() => setMobileSidebarOpen(true)}
            >
                <Menu className="h-5 w-5" />
            </button>
            {mobileSidebarOpen && (
                <div className="fixed inset-0 z-50 flex lg:hidden">
                    <button
                        type="button"
                        aria-label="Close navigation"
                        className="absolute inset-0 bg-slate-950/45"
                        onClick={() => setMobileSidebarOpen(false)}
                    />
                    <div className="relative z-10">
                        <Sidebar version={version} />
                    </div>
                </div>
            )}
            <main className="relative min-w-0 flex-1 overflow-auto p-4 pt-16 sm:p-6 sm:pt-16 lg:p-8">
                {navigationV2Enabled && sortedPinnedViews.length > 0 && (
                    <div className="mb-4 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Favorites</div>
                        <div className="flex flex-wrap gap-2">
                            {sortedPinnedViews.map((view) => (
                                <div
                                    key={view.id}
                                    className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 hover:border-slate-400 hover:text-slate-900"
                                >
                                    <button
                                        type="button"
                                        className="px-1"
                                        onClick={() => {
                                            trackNavigation(view.path, view.label, 'favorite');
                                            navigate(view.path);
                                        }}
                                    >
                                        {view.label}
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                                        onClick={async () => {
                                            await removePinnedView(view.id);
                                        }}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {navigationV2Enabled && (
                    <button
                        type="button"
                        onClick={() => setPaletteOpen(true)}
                        className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs text-slate-600 shadow-sm hover:text-slate-900"
                    >
                        <Search className="h-3.5 w-3.5" />
                        Search
                        <span className="rounded border border-slate-300 px-1.5 py-0.5 font-mono text-[10px]">⌘K</span>
                    </button>
                )}
                <Outlet />
                {navigationV2Enabled && paletteOpen && (
                    <div className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-sm p-4" onClick={() => setPaletteOpen(false)}>
                        <div
                            className="mx-auto mt-16 max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
                                <Search className="h-4 w-4 text-slate-500" />
                                <input
                                    autoFocus
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search pages, modules, and commands..."
                                    className="w-full bg-transparent outline-none text-sm text-slate-800"
                                />
                                <button type="button" onClick={() => setPaletteOpen(false)} className="text-slate-500 hover:text-slate-800">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="max-h-[420px] overflow-y-auto p-2">
                                {filteredCommands.length === 0 ? (
                                    <p className="px-3 py-8 text-center text-sm text-slate-500">No matching pages</p>
                                ) : (
                                    filteredCommands.map((item) => (
                                        <button
                                            type="button"
                                            key={`${item.group}:${item.path}`}
                                            className="w-full text-left rounded-lg px-3 py-2 hover:bg-slate-100"
                                            onClick={() => {
                                                trackNavigation(item.path, item.label, 'palette');
                                                navigate(item.path);
                                                setPaletteOpen(false);
                                                setQuery('');
                                            }}
                                        >
                                            <div className="text-sm font-medium text-slate-900">{item.label}</div>
                                            <div className="text-xs text-slate-500">{item.group} • {canonicalizeNavigationPath(item.path)}</div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {copilotEnabled && <AICopilot />}
            </main>
        </div>
    );
}
